/**
 * SpiceScheduleSummary: a SPICE schedule in words, the one rendering every
 * surface shares (the SPICE tab, the schedule dialog, the roster). One
 * line per kind with sessions, the kind as a bold label ("SPICE:",
 * "Friendly Visitor:") before its phrases, a dot in the kind's colour
 * before the label; "No schedule yet" for a plan with no sessions. The words themselves are formatSpiceScheduleLines'
 * (src/lib/spiceScheduleCopy.ts).
 */

import type { ReactNode } from 'react';
import {
  SPICE_KIND_LABELS,
  SPICE_NO_SCHEDULE_TEXT,
  formatSpiceScheduleLines,
  type SpiceScheduleSession,
} from '@/lib/spiceScheduleCopy';
import type { SpiceSessionKind } from '@/lib/spiceVisitPlansApi';
import { SpiceKindDot } from '@/components/clientRecord/SpiceKindMark';

/** The kind a line's label names (every label has a kind). */
function kindOf(label: string): SpiceSessionKind {
  const kinds = Object.keys(SPICE_KIND_LABELS) as SpiceSessionKind[];
  return kinds.find((k) => SPICE_KIND_LABELS[k] === label) ?? 'spice';
}

export interface SpiceScheduleSummaryProps {
  sessions: readonly SpiceScheduleSession[];
  className?: string;
}

export function SpiceScheduleSummary({ sessions, className }: SpiceScheduleSummaryProps): ReactNode {
  const lines = formatSpiceScheduleLines(sessions);
  return (
    <div data-testid="spice-schedule-summary" className={className}>
      {lines.length === 0 ? (
        <span>{SPICE_NO_SCHEDULE_TEXT}</span>
      ) : (
        lines.map((line) => (
          <div key={line.kind}>
            <SpiceKindDot kind={kindOf(line.kind)} className="mr-1.5 align-middle" />
            <span className="font-semibold text-ink">{line.kind}:</span> {line.text}
          </div>
        ))
      )}
    </div>
  );
}
